/**
 * ============================================================================
 * Andon 模块 API 工厂函数
 * ============================================================================
 * 
 * 📋 文件职责：
 * - 定义 Andon（安灯管理）模块的所有 API 请求方法
 * - 通过工厂模式实现请求客户端的依赖注入
 * - 提供完整的 TypeScript 类型安全
 * 
 * 🎯 核心概念：工厂模式
 * - 工厂函数：createAndonApi(client) - 接收请求客户端，返回 API 方法集合
 * - 依赖注入：不依赖具体的请求实现（axios、uni.request 等）
 * - 平台无关：Web 端和 App 端都可以使用，只需提供不同的适配器
 * 
 * 💡 设计理念：
 * - "给我一个能发送请求的工具，我就能为你生成所有 API 方法"
 * - 请求客户端是"工具"，API 方法是"产品"，工厂函数是"生产线"
 * 
 * 📖 使用方式：
 * 
 * **Web 端（Vben Admin + axios）**：
 * ```typescript
 * import { requestClient } from '#/api/request'
 * import { createAndonApi } from '@bayes-mes/shared/api/andon'
 * 
 * const webAdapter: IRequestClient = {
 *   get: (url, config) => requestClient.get(url, config).then(res => res.data),
 *   post: (url, data, config) => requestClient.post(url, data, config).then(res => res.data),
 *   put: (url, data, config) => requestClient.put(url, data, config).then(res => res.data),
 *   delete: (url, config) => requestClient.delete(url, config).then(res => res.data),
 * }
 * 
 * export const andonApi = createAndonApi(webAdapter)
 * 
 * // 使用
 * const calls = await andonApi.getAndonCallList({ page: 1 })
 * ```
 * 
 * **App 端（uni-app X）**：
 * ```typescript
 * import { createAndonApi } from '@bayes-mes/shared/api/andon'
 * import { uniAdapter } from '@/api/adapters/http'
 * 
 * export const andonApi = createAndonApi(uniAdapter)
 * ```
 * 
 * ⚠️ 注意事项：
 * - 所有方法都有完整的 TypeScript 类型定义
 * - 安灯接口在 auth:sanctum 路由组内（需登录 token）；除实时面板外各接口有独立权限码
 * - 注释详细，包括使用场景、参数、返回值、数据流向
 *
 * 🔄 更新时机：
 * - 后端新增 API 方法
 * - 后端修改参数或响应结构
 * - 后端修改路由路径
 *
 * @module api/andon/factory
 * @description Andon（安灯管理）模块的 API 工厂函数
 *
 * @source 对应后端文件:
 * - backend/routes/api.php (路由定义)
 * - backend/app/Modules/Andon/Controllers/*.php (控制器)
 *
 * @generated AI-assisted
 * @author AI Assistant
 * @created 2025-11-06
 * @last-updated 2026-10-08
 *
 * @change-log
 * - 2025-11-06: 初始创建，基于后端设计文档生成工厂函数
 * - 2026-10-08: 审查 P3-01 注释收口——接收人方法权限注释改回
 *   andon:notification-config:view/update（专属权限码）；修正实时面板
 *   "无需权限/不受 token 限制"的过时表述（实际在 Sanctum 组内）
 * 
 * @notes
 * - API 路径前缀: /api/v1/andon
 * - 使用工厂模式实现平台无关的 API 调用
 * - 支持完整的 TypeScript 类型推断
 * - IRequestClient 接口定义在 api/core/client.ts
 */

// ============================================================================
// 类型导入
// ============================================================================

import type {
  // 设备相关
  AndonDevice,
  AndonBinding,
  CreateAndonDeviceForm,
  UpdateAndonDeviceForm,
  CreateAndonBindingForm,
  UpdateAndonBindingForm,
  AndonDeviceQueryParams,
  AndonBindingQueryParams,
  AndonBindingFindResult,
  AndonDeviceSyncResult,
  AndonBindingMatchResult,
  
  // 呼叫相关
  AndonCall,
  CreateAndonCallForm,
  CompleteAndonCallForm,
  CancelAndonCallForm,
  AndonCallQueryParams,
  AndonPendingCallQueryParams,
  AndonMyCallQueryParams,
  
  // 呼叫类型配置
  AndonCallTypeConfig,
  CreateAndonCallTypeForm,
  UpdateAndonCallTypeForm,
  AndonCallTypeQueryParams,

  // 呼叫接收人配置
  AndonNotificationConfigItem,
  AndonNotificationRuleItem,
  UpdateAndonNotificationConfigForm,
  UpdateAndonNotificationRulesForm,
  
  // 实时面板
  AndonDashboard,
  AndonDashboardQueryParams,
  
  // 统计分析
  AndonStatsByType,
  AndonStatsByWorkstation,
  AndonStatsByLine,
  AndonStatsTrend,
  AndonStatsByPerson,
  AndonStatisticsQueryParams,
  AndonExportQueryParams,
  AndonTodayStats,
} from '../../types/andon'

import type { 
  PaginationResult,
} from '../../types/api'

// ============================================================================
// 导入统一的请求客户端接口
// ============================================================================
import type { IRequestClient } from '../core/client'

// ============================================================================
// 导入 API 路由定义
// ============================================================================
import { API_ROUTES } from '../routes'

// ============================================================================
// Andon API 工厂函数
// ============================================================================

/**
 * 创建 Andon 模块的 API 方法集合
 * 
 * @description
 * 这是一个工厂函数，接收一个请求客户端，返回一组 API 方法。
 * 
 * **工厂模式的核心思想**：
 * - 输入：一个能发送 HTTP 请求的客户端（符合 IRequestClient 接口）
 * - 输出：一组封装好的 API 方法（具有完整类型安全）
 * - 好处：同一套 API 定义，可以在不同平台使用（Web、App）
 * 
 * **路由结构（v2.0）**：
 * - 安灯子资源已分离为5个独立模块
 * - API_ROUTES.andon - 仪表板（主模块）
 * - API_ROUTES.andonDevice - 设备管理
 * - API_ROUTES.andonBinding - 绑定管理
 * - API_ROUTES.andonCall - 呼叫管理
 * - API_ROUTES.andonStatistic - 统计分析
 * 
 * @param client 请求客户端（必须符合 IRequestClient 接口）
 * @returns Andon 模块的所有 API 方法
 * 
 * @example
 * // Web 端使用
 * import { createAndonApi } from '@bayes-mes/shared/api/andon'
 * import { webAdapter } from '@/api/adapters/http'
 * 
 * export const andonApi = createAndonApi(webAdapter)
 * 
 * // 调用 API
 * const calls = await andonApi.getAndonCallList({ page: 1 })
 */
export function createAndonApi(client: IRequestClient) {
  return {
    // ========================================================================
    // 安灯设备管理（5个API）
    // ========================================================================

    /**
     * 获取安灯设备列表（分页）
     * 
     * @description
     * 获取安灯设备列表，支持分页和多条件筛选。
     * 
     * **使用场景**：
     * - 安灯设备管理页面的列表展示
     * - 设备选择器的数据源
     * 
     * **数据流向**：
     * ```
     * 前端 → GET /api/v1/andon/devices?page=1&per_page=20
     *      → AndonDeviceController::index()
     *      → AndonDeviceService::list()
     *      → 返回分页数据（AndonDevice[]）
     * ```
     * 
     * @param params 查询参数（所有字段可选）
     * @returns 分页的安灯设备列表
     * 
     * @example
     * const result = await andonApi.getAndonDeviceList({ page: 1, status: 1 })
     */
    getAndonDeviceList(params?: AndonDeviceQueryParams) {
      return client.get<PaginationResult<AndonDevice>>(API_ROUTES.andonDevice.list, { params })
    },

    /**
     * 获取安灯设备详情
     * 
     * @description
     * 根据设备ID获取安灯设备的详细信息，包括绑定关系。
     * 
     * **使用场景**：
     * - 设备详情页面
     * - 设备编辑表单的数据回显
     * 
     * **数据流向**：
     * ```
     * 前端 → GET /api/v1/andon/devices/{id}
     *      → AndonDeviceController::show()
     *      → 返回设备详情（AndonDevice，含 binding 关联）
     * ```
     * 
     * @param id 设备ID
     * @returns 设备详情
     * 
     * @example
     * const device = await andonApi.getAndonDeviceDetail(1)
     */
    getAndonDeviceDetail(id: number) {
      return client.get<AndonDevice>(API_ROUTES.andonDevice.detail(id))
    },

    /**
     * 创建安灯设备
     * 
     * @description
     * 创建新的安灯设备记录。安灯编号必须唯一。
     * 
     * **使用场景**：
     * - 安灯设备管理页面的新增功能
     * 
     * **数据流向**：
     * ```
     * 前端 → POST /api/v1/andon/devices
     *      → CreateAndonDeviceRequest::rules() (验证)
     *      → AndonDeviceController::store()
     *      → AndonDeviceService::create()
     *      → 返回创建的设备（AndonDevice）
     * ```
     * 
     * **注意事项**：
     * - andon_code、andon_name、andon_type 为必填字段
     * - andon_code 必须唯一
     * 
     * @param data 设备表单数据
     * @returns 创建的设备信息
     * 
     * @example
     * const device = await andonApi.createAndonDevice({
     *   andon_code: 'AD001',
     *   andon_name: '安灯设备1',
     *   andon_type: 'BUTTON',
     *   enable_horn: true,
     *   status: 1
     * })
     */
    createAndonDevice(data: CreateAndonDeviceForm) {
      return client.post<AndonDevice>(API_ROUTES.andonDevice.create, data)
    },

    /**
     * 更新安灯设备
     * 
     * @description
     * 更新安灯设备信息。所有字段都是可选的（部分更新）。
     * 
     * **使用场景**：
     * - 设备编辑功能
     * - 设备状态管理
     * 
     * @param id 设备ID
     * @param data 更新数据
     * @returns 更新后的设备信息
     * 
     * @example
     * const device = await andonApi.updateAndonDevice(1, {
     *   andon_name: '安灯设备1（已修改）',
     *   status: 0
     * })
     */
    updateAndonDevice(id: number, data: UpdateAndonDeviceForm) {
      return client.put<AndonDevice>(API_ROUTES.andonDevice.update(id), data)
    },

    /**
     * 删除安灯设备（软删除）
     * 
     * @description
     * 软删除安灯设备记录。如果设备已绑定，删除会失败。
     * 
     * **使用场景**：
     * - 设备管理页面的删除功能
     * 
     * @param id 设备ID
     * 
     * @example
     * await andonApi.deleteAndonDevice(1)
     */
    deleteAndonDevice(id: number) {
      return client.delete(API_ROUTES.andonDevice.delete(id))
    },

    /**
     * 从设备台账一键生成安灯设备并同步建立绑定
     *
     * @description
     * 后端读取设备台账（不含停用），逐台生成安灯设备（编号=设备编码、
     * 名称=设备名称、类型默认按钮式）并同步建立"生产设备↔安灯设备"绑定；
     * 设备档案挂有工位且工位未被占用时同时写入工位。幂等增量：安灯编号
     * 已存在（含软删）的设备自动跳过，可重复执行用于增量补充。
     *
     * **使用场景**：
     * - 新环境启用安灯时的初始化生成
     * - 设备台账新增设备后的增量同步
     *
     * @returns 汇总结果（总数/生成/绑定/工位绑定/跳过/失败明细）
     *
     * @example
     * const result = await andonApi.syncDevicesFromEquipment()
     * // { total: 144, created: 144, bound: 144, workstationBound: 96, skipped: 0, failed: [] }
     */
    syncDevicesFromEquipment() {
      return client.post<AndonDeviceSyncResult>(API_ROUTES.andonDevice.syncFromEquipment)
    },

    /**
     * 按编码匹配补绑安灯绑定
     *
     * @description
     * 给定一组编码（设备台账编码=安灯编号），为"同编码且未绑定"的台账设备
     * 与安灯设备补建绑定；只补绑已存在的匹配项，不生成安灯设备。设备台账
     * Excel 导入完成后由后端事件自动触发，安灯设备导入完成后由前端回调触发。
     *
     * @param codes 编码列表（max 2000）
     * @returns 汇总结果（匹配/绑定/工位/已绑定跳过/未匹配/失败明细）
     *
     * @example
     * const result = await andonApi.bindMatchingDevices(['SMT-001', 'SMT-002'])
     * // { total: 2, matched: 2, bound: 2, workstationBound: 1, skippedBound: 0, unmatched: [], failed: [] }
     */
    bindMatchingDevices(codes: string[]) {
      return client.post<AndonBindingMatchResult>(
        API_ROUTES.andonBinding.bindMatching,
        { codes },
      )
    },

    // ========================================================================
    // 设备绑定管理（6个API）
    // ========================================================================

    /**
     * 获取设备绑定列表（分页）
     * 
     * @description
     * 获取安灯设备绑定关系列表，支持分页和多条件筛选。
     * 
     * **使用场景**：
     * - 设备绑定管理页面的列表展示
     * 
     * @param params 查询参数
     * @returns 分页的绑定列表
     * 
     * @example
     * const result = await andonApi.getAndonBindingList({ page: 1 })
     */
    getAndonBindingList(params?: AndonBindingQueryParams) {
      return client.get<PaginationResult<AndonBinding>>(API_ROUTES.andonBinding.list, { params })
    },

    /**
     * 查询设备/工位的安灯绑定
     * 
     * @description
     * 根据设备ID或工位ID查询对应的安灯绑定关系。
     * 
     * **使用场景**：
     * - 查询设备是否已绑定安灯
     * - 查询工位是否已绑定安灯
     * - 员工上机时查询可用安灯
     * 
     * @param params 查询参数（equipment_id 或 workstation_id，二选一）
     * @returns 查询结果 `{ found, source, binding }`；未找到时 found=false、binding=null
     * 
     * @example
     * const { found, binding } = await andonApi.findAndonBinding({ equipment_id: 123 })
     */
    findAndonBinding(params: { equipment_id?: number; workstation_id?: number }) {
      return client.get<AndonBindingFindResult>(API_ROUTES.andonBinding.find, { params })
    },

    /**
     * 获取绑定详情
     * 
     * @description
     * 根据绑定ID获取绑定的详细信息。
     * 
     * @param id 绑定ID
     * @returns 绑定详情
     * 
     * @example
     * const binding = await andonApi.getAndonBindingDetail(1)
     */
    getAndonBindingDetail(id: number) {
      return client.get<AndonBinding>(API_ROUTES.andonBinding.detail(id))
    },

    /**
     * 创建设备绑定
     * 
     * @description
     * 创建新的安灯设备绑定关系。支持绑定设备或工位，或两者同时绑定。
     * 
     * **使用场景**：
     * - 绑定管理页面的新增功能
     * 
     * @param data 绑定表单数据
     * @returns 创建的绑定信息
     * 
     * @example
     * const binding = await andonApi.createAndonBinding({
     *   equipment_id: 123,
     *   andon_device_id: 10,
     *   workstation_id: 456,
     *   status: 1
     * })
     */
    createAndonBinding(data: CreateAndonBindingForm) {
      return client.post<AndonBinding>(API_ROUTES.andonBinding.create, data)
    },

    /**
     * 更新设备绑定
     * 
     * @description
     * 更新绑定关系的信息。
     * 
     * @param id 绑定ID
     * @param data 更新数据
     * @returns 更新后的绑定信息
     * 
     * @example
     * const binding = await andonApi.updateAndonBinding(1, { status: 0 })
     */
    updateAndonBinding(id: number, data: UpdateAndonBindingForm) {
      return client.put<AndonBinding>(API_ROUTES.andonBinding.update(id), data)
    },

    /**
     * 删除设备绑定
     * 
     * @description
     * 删除安灯设备绑定关系。
     * 
     * @param id 绑定ID
     * 
     * @example
     * await andonApi.deleteAndonBinding(1)
     */
    deleteAndonBinding(id: number) {
      return client.delete(API_ROUTES.andonBinding.delete(id))
    },

    // ========================================================================
    // 安灯呼叫管理（8个API）
    // ========================================================================

    /**
     * 创建安灯呼叫
     * 
     * @description
     * 员工发起安灯呼叫。系统会自动生成呼叫单号、记录呼叫时间、
     * 查找绑定的安灯设备、推送通知给责任人。
     * 
     * **使用场景**：
     * - App端员工上机后呼叫安灯
     * - 生产现场发现异常时快速呼叫
     * 
     * **数据流向**：
     * ```
     * 前端 → POST /api/v1/andon/calls
     *      → CreateAndonCallRequest::rules() (验证)
     *      → AndonCallController::store()
     *      → AndonCallService::create()
     *      → 生成呼叫单号
     *      → 查找安灯设备（设备优先，然后工位）
     *      → 推送通知给责任人
     *      → 触发IoT设备（可选）
     *      → 返回创建的呼叫（AndonCall）
     * ```
     * 
     * **注意事项**：
     * - equipment_id 和 workstation_id 至少提供一个
     * - call_type 为必填字段
     * - description 可为空，系统会标记为空描述用于统计
     * - images 最多3张
     * - 同一设备/工位同一时间只能有一个未完成的呼叫
     * 
     * @param data 呼叫表单数据
     * @returns 创建的呼叫信息
     * 
     * @example
     * const call = await andonApi.createAndonCall({
     *   equipment_id: 123,
     *   call_type: 'QUALITY',
     *   description: '产品表面有划痕',
     *   images: ['/storage/andon/image1.jpg']
     * })
     */
    createAndonCall(data: CreateAndonCallForm) {
      return client.post<AndonCall>(API_ROUTES.andonCall.create, data)
    },

    /**
     * 获取安灯呼叫列表（分页）
     * 
     * @description
     * 获取安灯呼叫记录列表，支持分页和多条件筛选。
     * 
     * **使用场景**：
     * - 呼叫记录查询页面
     * - 历史记录展示
     * 
     * @param params 查询参数
     * @returns 分页的呼叫列表
     * 
     * @example
     * const result = await andonApi.getAndonCallList({
     *   page: 1,
     *   status: 'PENDING',
     *   call_type: 'EQUIPMENT'
     * })
     */
    getAndonCallList(params?: AndonCallQueryParams) {
      return client.get<PaginationResult<AndonCall>>(API_ROUTES.andonCall.list, { params })
    },

    /**
     * 获取我的安灯呼叫列表
     * 
     * @description
     * 获取当前用户发起的安灯呼叫记录。
     * 
     * **使用场景**：
     * - App端员工查看自己的呼叫记录
     * - 个人呼叫历史
     * 
     * @param params 查询参数
     * @returns 分页的呼叫列表
     * 
     * @example
     * const result = await andonApi.getMyAndonCalls({ page: 1 })
     */
    getMyAndonCalls(params?: AndonMyCallQueryParams) {
      return client.get<PaginationResult<AndonCall>>(API_ROUTES.andonCall.my, { params })
    },

    /**
     * 获取待处理的安灯呼叫列表
     * 
     * @description
     * 获取所有待响应的安灯呼叫，按优先级和时间排序。
     * 
     * **使用场景**：
     * - 责任人查看待处理的呼叫
     * - 班组长监控未响应的呼叫
     * 
     * @param params 查询参数（仅 limit：默认 20，上限 100）
     * @returns 待响应呼叫数组（不分页）
     * 
     * @example
     * const calls = await andonApi.getPendingAndonCalls({ limit: 50 })
     */
    getPendingAndonCalls(params?: AndonPendingCallQueryParams) {
      return client.get<AndonCall[]>(API_ROUTES.andonCall.pending, { params })
    },

    /**
     * 获取安灯呼叫详情
     * 
     * @description
     * 根据呼叫ID获取呼叫的详细信息。
     * 
     * @param id 呼叫ID
     * @returns 呼叫详情
     * 
     * @example
     * const call = await andonApi.getAndonCallDetail(1)
     */
    getAndonCallDetail(id: number) {
      return client.get<AndonCall>(API_ROUTES.andonCall.detail(id))
    },

    /**
     * 响应安灯呼叫
     * 
     * @description
     * 责任人响应安灯呼叫。使用数据库行锁实现抢单机制，
     * 只有一个人能响应成功，其他人会收到"已被响应"提示。
     * 
     * **使用场景**：
     * - 班组长/责任人点击响应按钮
     * - 抢单机制，防止多人同时响应
     * 
     * **数据流向**：
     * ```
     * 前端 → POST /api/v1/andon/calls/{id}/respond
     *      → AndonCallController::respond()
     *      → AndonCallService::respond()
     *      → 使用行锁（lockForUpdate）
     *      → 检查状态是否为PENDING
     *      → 更新响应人、响应时间
     *      → 计算响应时长
     *      → 推送通知给呼叫人
     *      → 返回更新后的呼叫（AndonCall）
     * ```
     * 
     * @param id 呼叫ID
     * @param data 响应数据，包含扫描的设备编码用于验证响应人已到现场
     * @returns 更新后的呼叫信息
     * 
     * @example
     * const call = await andonApi.respondAndonCall(1, { equipment_code: 'EQ001' })
     */
    respondAndonCall(id: number, data: { equipment_code: string }) {
      return client.post<AndonCall>(API_ROUTES.andonCall.respond(id), data)
    },

    /**
     * 完成安灯呼叫
     * 
     * @description
     * 责任人处理完成后关闭安灯。需要填写处理结果。
     * 
     * **使用场景**：
     * - 问题处理完成后关闭呼叫
     * 
     * @param id 呼叫ID
     * @param data 处理结果
     * @returns 更新后的呼叫信息
     * 
     * @example
     * const call = await andonApi.completeAndonCall(1, {
     *   handle_result: '已更换新产品，旧产品报废处理'
     * })
     */
    completeAndonCall(id: number, data: CompleteAndonCallForm) {
      return client.post<AndonCall>(API_ROUTES.andonCall.complete(id), data)
    },

    /**
     * 取消安灯呼叫
     * 
     * @description
     * 取消安灯呼叫。呼叫人或管理员可以取消呼叫。
     * 
     * **使用场景**：
     * - 误操作，需要取消呼叫
     * - 问题已自行解决
     * 
     * @param id 呼叫ID
     * @param data 取消原因
     * @returns 更新后的呼叫信息
     * 
     * @example
     * const call = await andonApi.cancelAndonCall(1, {
     *   cancel_reason: '误操作，问题已自行解决'
     * })
     */
    cancelAndonCall(id: number, data: CancelAndonCallForm) {
      return client.post<AndonCall>(API_ROUTES.andonCall.cancel(id), data)
    },

    // ========================================================================
    // 实时面板（2个API，无需权限）
    // ========================================================================

    /**
     * 获取实时面板数据（无需权限）
     * 
     * @description
     * 获取安灯系统的实时面板数据，包括：
     * - 今日概况统计
     * - 当前告警列表
     * - 工位状态地图
     * 
     * **特殊说明**：
     * - ✅ **无需权限**：此接口不受token限制，可公开访问
     * - ✅ **支持WebSocket实时推送**：数据自动更新
     * - ✅ **全屏模式**：适合挂大屏展示
     * - ✅ **跨平台**：Web端和App端都支持
     * 
     * **使用场景**：
     * - 生产现场大屏展示
     * - 实时监控所有工位状态
     * - 管理者快速了解当前异常情况
     * 
     * @param params 查询参数（用于筛选车间、产线等）
     * @returns 实时面板数据
     * 
     * @example
     * // 获取全部数据
     * const dashboard = await andonApi.getAndonDashboard()
     * 
     * // 筛选特定车间
     * const dashboard = await andonApi.getAndonDashboard({ workshop_id: 1 })
     */
    getAndonDashboard(params?: AndonDashboardQueryParams) {
      return client.get<AndonDashboard>(API_ROUTES.andon.dashboard, { params })
    },

    /**
     * 获取当前告警列表（无需权限）
     * 
     * @description
     * 获取当前所有未完成的安灯呼叫（告警）。
     * 
     * **特殊说明**：
     * - ✅ **无需权限**：此接口不受token限制，可公开访问
     * 
     * **使用场景**：
     * - 实时面板的告警列表
     * - 简化的告警展示
     * 
     * @param params 查询参数
     * @returns 告警列表
     * 
     * @example
     * const alerts = await andonApi.getAndonAlerts({ limit: 10 })
     */
    getAndonAlerts(params?: AndonDashboardQueryParams) {
      return client.get<AndonCall[]>(API_ROUTES.andon.dashboardAlerts, { params })
    },

    // ========================================================================
    // 统计分析（7个API）
    // ========================================================================

    /**
     * 获取今日概况统计
     * 
     * @description
     * 获取今日的安灯呼叫概况统计。
     * 
     * @param params 统计参数
     * @returns 今日统计数据
     * 
     * @example
     * const stats = await andonApi.getAndonStatsToday()
     */
    getAndonStatsToday(params?: Pick<AndonStatisticsQueryParams, 'factory_id' | 'workshop_id' | 'line_id'>) {
      return client.get<AndonTodayStats>(API_ROUTES.andonStatistic.list, { params })
    },

    /**
     * 按类型统计
     * 
     * @description
     * 按呼叫类型统计安灯数据，包括数量、占比、时长等。
     * 
     * @param params 统计参数
     * @returns 按类型的统计数据
     * 
     * @example
     * const stats = await andonApi.getAndonStatsByType({
     *   start_date: '2025-11-01',
     *   end_date: '2025-11-30'
     * })
     */
    getAndonStatsByType(params?: AndonStatisticsQueryParams) {
      return client.get<AndonStatsByType[]>(API_ROUTES.andonStatistic.statisticsByType, { params })
    },

    /**
     * 按工位统计
     * 
     * @description
     * 按工位统计安灯数据，返回TOP10高频异常工位。
     * 
     * @param params 统计参数
     * @returns 按工位的统计数据
     * 
     * @example
     * const stats = await andonApi.getAndonStatsByWorkstation()
     */
    getAndonStatsByWorkstation(params?: AndonStatisticsQueryParams) {
      return client.get<AndonStatsByWorkstation[]>(API_ROUTES.andonStatistic.statisticsByWorkstation, { params })
    },

    /**
     * 按产线统计
     * 
     * @description
     * 按产线统计安灯数据，用于产线对比分析。
     * 
     * @param params 统计参数
     * @returns 按产线的统计数据
     * 
     * @example
     * const stats = await andonApi.getAndonStatsByLine()
     */
    getAndonStatsByLine(params?: AndonStatisticsQueryParams) {
      return client.get<AndonStatsByLine[]>(API_ROUTES.andonStatistic.statisticsByLine, { params })
    },

    /**
     * 时间趋势统计
     * 
     * @description
     * 获取安灯呼叫的时间趋势数据（7天/30天/24小时）。
     * 
     * @param params 统计参数（必须指定 period）
     * @returns 时间趋势数据
     * 
     * @example
     * const stats = await andonApi.getAndonStatsTrend({ period: '7d' })
     */
    getAndonStatsTrend(params: AndonStatisticsQueryParams & { period: '7d' | '30d' | '24h' }) {
      return client.get<AndonStatsTrend>(API_ROUTES.andonStatistic.trend, { params })
    },

    /**
     * 按人员统计
     * 
     * @description
     * 按响应人员统计处理效率数据。
     * 
     * @param params 统计参数
     * @returns 按人员的统计数据
     * 
     * @example
     * const stats = await andonApi.getAndonStatsByPerson()
     */
    getAndonStatsByPerson(params?: AndonStatisticsQueryParams) {
      return client.get<AndonStatsByPerson[]>(API_ROUTES.andonStatistic.statisticsByPerson, { params })
    },

    /**
     * 导出统计数据（Excel）
     * 
     * @description
     * 根据查询条件导出安灯统计数据到 Excel 文件。
     * 
     * **使用场景**：
     * - 导出统计报表
     * - 数据分析和备份
     * 
     * @param params 导出参数
     * @returns Excel 文件（Blob）
     * 
     * @example
     * const blob = await andonApi.exportAndonStatistics({
     *   type: 'statistics',
     *   start_date: '2025-11-01',
     *   end_date: '2025-11-30'
     * })
     * // 下载文件
     * const url = URL.createObjectURL(blob)
     * const a = document.createElement('a')
     * a.href = url
     * a.download = '安灯统计数据.xlsx'
     * a.click()
     * URL.revokeObjectURL(url)
     */
    exportAndonStatistics(params?: AndonExportQueryParams) {
      // responseReturn:'body' 必须显式声明：后端导出返回裸二进制流（无
      // {code:0} 信封），缺省时业务码拦截器会在 Blob 上取 code 失败而抛错，
      // 导致导出永远失败并弹"导出失败"双 toast。
      return client.post(API_ROUTES.andonStatistic.export, params, {
        responseType: 'blob',
        responseReturn: 'body',
      })
    },

    // ========================================================================
    // 呼叫类型配置（4个API）
    // ========================================================================

    /**
     * 获取呼叫类型列表
     *
     * @description
     * 默认仅返回启用类型（呼叫下拉场景）；传 all=true 返回含停用全量（维护页）。
     *
     * **使用场景**：
     * - App/Web 发起呼叫的类型选择数据源
     * - Web 呼叫类型维护页列表
     *
     * @param params all: 是否包含停用类型
     * @returns 呼叫类型配置数组（按 sort_order 升序）
     */
    getAndonCallTypeList(params?: AndonCallTypeQueryParams) {
      return client.get<AndonCallTypeConfig[]>(API_ROUTES.andonCallType.list, { params })
    },

    /**
     * 创建呼叫类型
     *
     * @param data 类型配置（type_code 全局唯一，创建后不可修改）
     */
    createAndonCallType(data: CreateAndonCallTypeForm) {
      return client.post<AndonCallTypeConfig>(API_ROUTES.andonCallType.create, data)
    },

    /**
     * 更新呼叫类型
     *
     * @description
     * type_code 创建后不可修改，传入也会被后端忽略。
     */
    updateAndonCallType(id: number, data: UpdateAndonCallTypeForm) {
      return client.put<AndonCallTypeConfig>(API_ROUTES.andonCallType.update(id), data)
    },

    /**
     * 删除呼叫类型
     *
     * @description
     * 仅零呼叫引用的类型可删除；被引用类型返回 400（code=4005），应改为停用。
     */
    deleteAndonCallType(id: number) {
      return client.delete(API_ROUTES.andonCallType.delete(id))
    },

    /**
     * 读取全部呼叫类型的接收人配置（含停用类型）
     *
     * @description
     * 按类型独立：部门/角色/岗位/指定用户 四维并集 + 车间/产线负责人开关 + 超时升级 1..3 级。
     * 权限：andon:notification-config:view（专属权限码，非 call-type）。
     */
    getAndonNotificationConfig() {
      return client.get<AndonNotificationConfigItem[]>(API_ROUTES.andonNotificationConfig.list)
    },

    /**
     * 更新单个呼叫类型的接收人配置（整体替换语义，只更新传入的组）
     *
     * @description
     * 权限：andon:notification-config:update（专属权限码，非 call-type）。
     * escalate_levels 未传的级别由后端归一为空维度。
     */
    updateAndonNotificationConfig(typeCode: string, data: UpdateAndonNotificationConfigForm) {
      return client.put<AndonNotificationConfigItem>(
        API_ROUTES.andonNotificationConfig.update(typeCode),
        data,
      )
    },

    /**
     * 读取通知事件规则（全局 5 类事件启停 + 四件套必备渠道）
     *
     * 权限：andon:notification-config:view。
     */
    getAndonNotificationRules() {
      return client.get<AndonNotificationRuleItem[]>(API_ROUTES.andonNotificationConfig.rules)
    },

    /**
     * 批量更新事件启停（渠道由服务端固定写入四件套必备）
     *
     * 权限：andon:notification-config:update。
     */
    updateAndonNotificationRules(data: UpdateAndonNotificationRulesForm) {
      return client.put<AndonNotificationRuleItem[]>(
        API_ROUTES.andonNotificationConfig.rules,
        data,
      )
    },
  }
}

// ============================================================================
// 类型导出（供类型推断使用）
// ============================================================================

/**
 * Andon API 实例类型
 * 
 * @description
 * 通过 ReturnType 工具类型自动推断出 API 实例的类型。
 * 这样在使用时可以获得完整的类型提示。
 */
export type AndonApi = ReturnType<typeof createAndonApi>
